import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  inject,
  DestroyRef,
  Injector,
  runInInjectionContext,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Auth, User, user } from '@angular/fire/auth';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { profile_model_name } from '@padspin/firestore-profile';
import { CustomClaims, CustomClaimsSchema } from '@padspin/rbac';
import { Observable, of } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  pairwise,
  shareReplay,
  catchError,
  distinctUntilChanged,
} from 'rxjs/operators';
import { DEBUG, ERROR, WARN } from '@padspin/ui-logger';

/**
 * Service that watches the authenticated user's profile document in Firestore
 * and automatically refreshes the auth token when claims change.
 *
 * - Only activates for authenticated, non-anonymous users
 * - Uses Firestore docData() for real-time updates with RxJS integration
 * - Detects claims changes and calls getIdToken(true) to force token refresh
 * - Zero performance impact on unauthenticated or anonymous users
 */
@Injectable({ providedIn: 'root' })
export class ProfileClaimsWatcherService {
  private readonly error = inject(ERROR);
  private readonly debug = inject(DEBUG);
  private readonly warn = inject(WARN);
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly profileClaims$: Observable<CustomClaims | null> =
    isPlatformBrowser(this.platformId)
      ? runInInjectionContext(this.injector, () => user(this.auth)).pipe(
          // Only proceed if user exists and is not anonymous
          filter((user): user is User => !!user && !user.isAnonymous),
          // Switch to watching the user's profile document
          switchMap((user) => {
            return runInInjectionContext(this.injector, () => {
              const profileDocRef = doc(
                this.firestore,
                profile_model_name,
                user.uid,
              );
              return docData(profileDocRef).pipe(
                // Handle errors (e.g., permission-denied after logout) without terminating the stream
                catchError((err) => {
                  this.warn(
                    '[ProfileClaimsWatcher] Error reading profile document (user may have logged out):',
                    err.code || err.message,
                  );
                  return of(null);
                }),
              );
            });
          }),
          // Extract claims from profile data
          map((profileData) => {
            // If profileData is null, it means we couldn't read the profile (e.g., user logged out)
            if (profileData === null) {
              return null;
            }

            const claims = profileData?.['claims'] as CustomClaims | undefined;
            return claims ?? null;
          }),
          // Deduplicate emissions before logging (Firestore can emit same value multiple times)
          distinctUntilChanged((prev, curr) => {
            // Both null - no change
            if (prev === null && curr === null) return true;
            // One is null - change
            if (prev === null || curr === null) return false;
            // Compare using schema validation
            try {
              return (
                JSON.stringify(CustomClaimsSchema.parse(prev)) ===
                JSON.stringify(CustomClaimsSchema.parse(curr))
              );
            } catch {
              return false;
            }
          }),
          // Log only when claims actually change
          map((claims) => {
            if (claims === null) {
              this.debug('[ProfileClaimsWatcher] No claims available');
            } else {
              this.debug('[ProfileClaimsWatcher] Retrieved claims:', claims);
            }
            return claims;
          }),
          // Share the subscription across all consumers to avoid duplicate Firestore listeners
          shareReplay({ bufferSize: 1, refCount: false }),
        )
      : of(null);

  private readonly profileClaimsChange$: Observable<CustomClaims> =
    isPlatformBrowser(this.platformId)
      ? this.profileClaims$.pipe(
          // Filter out null claims
          filter((claims): claims is CustomClaims => !!claims),
          // Only emit when claims actually change (prevents duplicate Firestore emissions)
          distinctUntilChanged((prev, curr) => {
            const prevResult = CustomClaimsSchema.safeParse(prev);
            const currResult = CustomClaimsSchema.safeParse(curr);
            if (prevResult.success && currResult.success) {
              return (
                JSON.stringify(prevResult.data) ===
                JSON.stringify(currResult.data)
              );
            }
            // If both failed, treat as different (could compare error messages if desired)
            return false;
          }),
          // Use pairwise to get access to both old and new claims (for debugging/logging)
          pairwise(),
          // Log the change for visibility
          map(([prevClaims, currClaims]) => {
            this.debug(
              '[ProfileClaimsWatcher] Claims changed from',
              prevClaims,
              'to',
              currClaims,
            );
            return currClaims;
          }),
          takeUntilDestroyed(this.destroyRef),
          // Share the subscription between toSignal and initializeWatcher
          shareReplay({ bufferSize: 1, refCount: false }),
        )
      : of(CustomClaimsSchema.parse({}));

  readonly customClaims = toSignal(this.profileClaims$);

  readonly customClaimsChange = toSignal<CustomClaims>(
    this.profileClaimsChange$,
  );

  constructor() {
    this.initializeWatcher();
  }

  private initializeWatcher(): void {
    // Watch the auth user observable
    if (!isPlatformBrowser(this.platformId)) {
      this.debug(
        "[ProfileClaimsWatcher] The current user's profile/claims do not change on the server",
      );
      return;
    }
    this.profileClaimsChange$.subscribe({
      next: () => this.refreshAuthToken(),
      error: (err) => {
        // This should rarely happen now since errors are handled upstream
        this.error(
          '[ProfileClaimsWatcher] Unexpected error in claims watcher:',
          err,
        );
      },
    });
  }

  private async refreshAuthToken(): Promise<void> {
    this.debug(`[ProfileClaimsWatcher] Claims changed, refreshing auth token`);
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      this.warn('[ProfileClaimsWatcher] No current user to refresh token');
      return;
    }

    try {
      // Force token refresh with getIdToken(true)
      this.debug('[ProfileClaimsWatcher] Forcing token refresh');
      await currentUser.getIdTokenResult(true);
    } catch (error) {
      this.error('[ProfileClaimsWatcher] Failed to refresh token:', error);
    }
  }
}
